import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import api from "../lib/api";
import { Reveal } from "../components/Reveal";
import { usePageMeta } from "../lib/seo";

export default function CaseStudies() {
  usePageMeta({
    title: "Case Studies",
    description:
      "Explore AI projects we've delivered: ESG GraphRAG intelligence, a 500K+ dataset LCA/EPD platform, WhatsApp AI automation, an enterprise web chatbot, and more.",
    path: "/case-studies",
  });
  const [cases, setCases] = useState([]);

  useEffect(() => {
    api.get("/case-studies").then((r) => setCases(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="case-studies-page">
      <section className="relative grid-bg border-b border-white/10">
        <div className="absolute inset-0 radial-glow pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 pt-36 pb-20 sm:pt-44">
          <Reveal>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-primary mb-4">Case Studies</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold max-w-3xl leading-[1.07]">
              AI projects we're proud of.
            </h1>
            <p className="mt-6 text-slate-400 max-w-2xl text-lg leading-relaxed">
              A look at how we've helped companies in India and across the world ship intelligent products
              that deliver measurable outcomes.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-20 sm:py-28">
        <div className="grid gap-7 md:grid-cols-2">
          {cases.map((c, i) => (
            <Reveal key={c.slug} delay={(i % 2) * 90}>
              <Link to={`/case-studies/${c.slug}`} data-testid={`case-card-${c.slug}`} className="group block h-full border border-white/10 overflow-hidden bg-[#0F111A] hover:border-primary/50 transition-all duration-300 hover:-translate-y-1">
                <div className="relative h-56 overflow-hidden">
                  <img src={c.cover_image} alt={c.title} className="w-full h-full object-cover opacity-70 group-hover:opacity-95 group-hover:scale-105 transition-all duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F111A] via-[#0F111A]/30 to-transparent" />
                  <span className="absolute top-4 left-4 text-xs bg-black/50 backdrop-blur border border-white/10 px-3 py-1 text-slate-200">{c.category}</span>
                  <div className="absolute bottom-4 left-4 flex items-baseline gap-2">
                    <span className="font-heading text-3xl font-semibold text-white drop-shadow">{c.metric_value}</span>
                    <span className="text-xs text-slate-300">{c.metric_label}</span>
                  </div>
                </div>
                <div className="p-7">
                  <h3 className="font-heading text-xl font-semibold text-white leading-snug group-hover:text-primary transition-colors">{c.title}</h3>
                  <p className="text-sm text-slate-400 mt-3 leading-relaxed line-clamp-3">{c.summary}</p>
                  <div className="flex flex-wrap gap-2 mt-5">
                    {c.technologies.slice(0, 4).map((t) => (
                      <span key={t} className="text-xs font-mono text-slate-400 border border-white/10 px-2.5 py-1">{t}</span>
                    ))}
                    {c.technologies.length > 4 && (
                      <span className="text-xs font-mono text-slate-500 px-2.5 py-1">+{c.technologies.length - 4}</span>
                    )}
                  </div>
                  <span className="inline-flex items-center gap-1.5 mt-6 text-sm text-primary font-medium">
                    Read case study <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
